import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Card, CardBody } from "reactstrap";
import "./ReportComponents.css";
import MonthlyPerformanceFilter from "./common/MonthlyPerformanceFilter";
import MonthlyPerformanceCharts, { SummaryCards } from "./common/MonthlyPerformanceCharts";
import MonthlyPerformancePatientTable from "./common/MonthlyPerformancePatientTable";
import MonthlyPerformanceShimmer from "./common/MonthlyPerformanceShimmer";
import CptDashboardSkeletonOverlay from "../common/CptDashboardSkeletonOverlay";
import { getCptAnalyticsTimezone } from "../common/cptAnalyticsTimezone";
import {
  getDefaultFilter,
  getMonthRange,
  formatMonthLabel
} from "./common/monthlyPerformanceUtils";

const PatientCPTRequirementsMet = ({
  dispatch,
  monthlyPerformanceReportData,
  ongoingMonthlyPerformanceReportRequest
}) => {
  const [filters, setFilters] = useState(getDefaultFilter());
  const [searchText, setSearchText] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const prevLoadingRef = useRef(false);

  const fetchReport = (nextFilters = filters) => {
    const baseMonthRange = getMonthRange(nextFilters.baseYear, nextFilters.baseMonth);
    const comparedMonthRange = getMonthRange(nextFilters.comparedYear, nextFilters.comparedMonth);

    dispatch({
      type: "DO_GET_MONTHLY_PERFORMANCE_REPORT",
      payload: {
        performanceType: nextFilters.performanceType,
        timezone: getCptAnalyticsTimezone(),
        baseMonthStartDate: baseMonthRange.startDate,
        baseMonthEndDate: baseMonthRange.endDate,
        comparedMonthStartDate: comparedMonthRange.startDate,
        comparedMonthEndDate: comparedMonthRange.endDate
      }
    });
  };

  useEffect(() => {
    if (prevLoadingRef.current && !ongoingMonthlyPerformanceReportRequest) {
      setIsInitialLoading(false);
    }
    prevLoadingRef.current = ongoingMonthlyPerformanceReportRequest;
  }, [ongoingMonthlyPerformanceReportRequest]);

  const reportData = monthlyPerformanceReportData || {};
  const dashboard = reportData.dashboard || {};
  const comparison = dashboard?.comparison || {};
  const summary = reportData.summary || {};
  const patients = Array.isArray(reportData.patients) ? reportData.patients : [];
  const baseMonthLabel = formatMonthLabel(filters.baseYear, filters.baseMonth);
  const comparedMonthLabel = formatMonthLabel(filters.comparedYear, filters.comparedMonth);
  const summaryWithFallback = {
    totalPatients: summary.totalPatients ?? reportData.total ?? patients.length ?? 0,
    fullyBillable: summary.fullyBillable ?? dashboard?.comparedMonth?.fullyBillable ?? 0,
    partial: summary.partial ?? dashboard?.comparedMonth?.partial ?? 0,
    notBillable: summary.notBillable ?? dashboard?.comparedMonth?.notBillable ?? 0
  };

  const derivedDashboard = useMemo(
    () => ({
      ...dashboard,
      baseMonth: {
        ...dashboard?.baseMonth,
        label: reportData.baseMonth || formatMonthLabel(filters.baseYear, filters.baseMonth)
      },
      comparedMonth: {
        ...dashboard?.comparedMonth,
        label: reportData.comparedMonth || formatMonthLabel(filters.comparedYear, filters.comparedMonth)
      }
    }),
    [dashboard, filters, reportData.baseMonth, reportData.comparedMonth]
  );

  const hasLoadedOnce = Boolean(monthlyPerformanceReportData);
  const showShimmer =
    (isInitialLoading && ongoingMonthlyPerformanceReportRequest) ||
    (ongoingMonthlyPerformanceReportRequest && !monthlyPerformanceReportData);

  const isRefetching = ongoingMonthlyPerformanceReportRequest && !showShimmer;

  return (
    <div className={`report-container cpt-dash-report-surface ${isRefetching ? "cpt-dash-report-surface--busy" : ""}`}>
      {isRefetching && (
        <CptDashboardSkeletonOverlay>
          <MonthlyPerformanceShimmer />
        </CptDashboardSkeletonOverlay>
      )}
      {/* <div className="enterprise-header">
        <div className="header-left">
          <h1 className="page-title-enterprise">
            <i className="mdi mdi-clipboard-text-outline"></i>
            Patient CPT Code Requirements Met Report
          </h1>
          <p className="page-subtitle-enterprise">
            Compare base and previous month performance to ensure patients meet both technical and professional CPT components.
          </p>
        </div>
      </div> */}

      <MonthlyPerformanceFilter
        filters={filters}
        onChange={setFilters}
        onApply={() => {
          setSearchText("");
          fetchReport(filters);
        }}
        loading={ongoingMonthlyPerformanceReportRequest}
      />

      {showShimmer ? (
        <MonthlyPerformanceShimmer />
      ) : !hasLoadedOnce ? (
        <div className="cpt-select-prompt">
          <div className="cpt-select-prompt-icon">
            <i className="mdi mdi-calendar-search"></i>
          </div>
          <h5>Select a month to view CPT requirements met</h5>
          <p>Choose base and compared months above, then click <strong>Filter</strong> to load the report.</p>
        </div>
      ) : (
        <>
          <SummaryCards summary={summaryWithFallback} comparison={comparison} dashboard={derivedDashboard} />
          <MonthlyPerformanceCharts dashboard={derivedDashboard} />

          <Card className="mpr-patient-list-section">
            <CardBody>
              <div className="chart-header-modern mpr-list-header">
                <div className="chart-icon-modern">
                  <i className="mdi mdi-account-group-outline"></i>
                </div>
                <div className="chart-header-copy-modern">
                  <h4>Patient Performance List</h4>
                  <p>
                    Search and export patients by monthly CPT component outcomes.
                  </p>
                </div>
              </div>
              <MonthlyPerformancePatientTable
                patients={patients}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                fileSuffix={`monthly-performance-${filters.comparedYear}-${filters.comparedMonth}`}
                baseMonthLabel={baseMonthLabel}
                comparedMonthLabel={comparedMonthLabel}
              />
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ careprovider }) => ({
  monthlyPerformanceReportData: careprovider.monthlyPerformanceReportData,
  ongoingMonthlyPerformanceReportRequest: careprovider.ongoingMonthlyPerformanceReportRequest
});

export default connect(mapStateToProps)(PatientCPTRequirementsMet);
